import {
  CalendarOutlined,
  FolderOutlined,
  InfoCircleOutlined,
  TagsOutlined,
  TeamOutlined,
  UserOutlined
} from "@ant-design/icons"
import { Card, Divider, Flex, Select, Typography } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"

import { neutral } from "../../../../components/widgets/tokens.ts"
import { filterByLabelOrValue } from "../../../DashboardPage/components/Portfolio/FilterBar.tsx"
import { useAppDispatch } from "../../../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../../../hooks/use-app-selector.ts"
import {
  setReportCompare,
  setReportFilters,
  setReportWindow
} from "../../helpers/reportDefinition.ts"
import { useReportFilterOptions } from "../../hooks/useReportFilterOptions.ts"
import { reportCanvasApplyDefinition } from "../../redux/reportCanvas.slice.ts"
import {
  isCustomWindow,
  type ReportCompareValue,
  type ReportFilterDimension,
  type ReportWindow
} from "../../types/canvas.types.ts"
import { ReportCompareControl } from "../canvas/ReportCompareControl.tsx"
import { ReportDateRangeControl } from "../canvas/ReportDateRangeControl.tsx"

const { Text } = Typography

const DIM_LABEL_KEYS: Record<ReportFilterDimension, string> = {
  project: "reports.filters.project",
  pm: "reports.filters.pm",
  client: "reports.filters.client",
  project_type: "reports.filters.projectType",
  project_status: "reports.filters.projectStatus",
  status: "reports.filters.status",
  state: "reports.filters.state"
}

const FilterCard = ({
  icon,
  title,
  children
}: {
  icon: React.ReactNode
  title: string
  children: React.ReactNode
}) => (
  <Card size="small" title={<Flex align="center" gap="small">{icon}<span>{title}</span></Flex>}>
    {children}
  </Card>
)

export const ReportFiltersStep = () => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const definition = useAppSelector((s) => s.reportCanvas.definition)
  const { options, loading } = useReportFilterOptions()

  const filters = definition?.filters ?? {}

  const setWindow = (window: ReportWindow) => {
    if (!definition) return
    dispatch(reportCanvasApplyDefinition(setReportWindow(definition, window)))
  }
  const setCompare = (compare: ReportCompareValue) => {
    if (!definition) return
    dispatch(reportCanvasApplyDefinition(setReportCompare(definition, compare)))
  }
  const setDim = (dimension: ReportFilterDimension, value: string[]) => {
    if (!definition) return
    dispatch(
      reportCanvasApplyDefinition(
        setReportFilters(definition, { ...filters, [dimension]: value })
      )
    )
  }

  const dimSelect = (dimension: ReportFilterDimension) => (
    <Flex vertical gap={4} className="min-w-0 flex-1">
      <Text type="secondary" style={{ fontSize: 12 }}>
        {t(DIM_LABEL_KEYS[dimension])}
      </Text>
      <Select<string[]>
        mode="multiple"
        allowClear
        showSearch
        loading={loading}
        maxTagCount="responsive"
        filterOption={filterByLabelOrValue}
        placeholder={t(DIM_LABEL_KEYS[dimension])}
        value={filters[dimension] ?? []}
        options={options[dimension]}
        onChange={(value) => setDim(dimension, value)}
        style={{ width: "100%" }}
      />
    </Flex>
  )

  const summaryRow = (
    icon: React.ReactNode,
    title: string,
    value: string
  ) => (
    <Flex vertical gap={2}>
      <Flex align="center" gap="small">
        <span style={{ color: neutral.muted }}>{icon}</span>
        <Text strong>{title}</Text>
      </Flex>
      <Text type="secondary" style={{ fontSize: 13, paddingLeft: 22 }}>
        {value}
      </Text>
    </Flex>
  )

  const dimSummary = (dimension: ReportFilterDimension): string => {
    const count = filters[dimension]?.length ?? 0
    return count > 0 ? String(count) : t("reports.filtersStep.summary.all")
  }

  const windowSummary = (): string => {
    const window = definition?.window
    if (!window) return t("reports.window.this_month")
    if (isCustomWindow(window)) return `${window.from} – ${window.to}`
    return t(`reports.window.${window.preset}`)
  }

  return (
    <Flex gap="large" align="start" wrap="wrap">
      <Flex vertical gap="middle" className="min-w-0 flex-1">
        <FilterCard
          icon={<CalendarOutlined />}
          title={t("reports.filtersStep.cards.dateRange")}
        >
          <Flex gap="middle" wrap="wrap" align="flex-start">
            <Flex vertical gap={4}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {t("reports.filtersStep.fields.datePreset")}
              </Text>
              <ReportDateRangeControl
                window={definition?.window}
                onChange={setWindow}
              />
            </Flex>
            <Flex vertical gap={4}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {t("reports.filtersStep.fields.compareTo")}
              </Text>
              <ReportCompareControl
                compare={definition?.compare}
                onChange={setCompare}
              />
            </Flex>
          </Flex>
        </FilterCard>

        <FilterCard
          icon={<FolderOutlined />}
          title={t("reports.filtersStep.cards.projects")}
        >
          <Flex gap="middle" wrap="wrap">
            {dimSelect("project")}
            {dimSelect("project_status")}
            {dimSelect("project_type")}
          </Flex>
        </FilterCard>

        <FilterCard
          icon={<UserOutlined />}
          title={t("reports.filtersStep.cards.customers")}
        >
          <Flex gap="middle" wrap="wrap">
            {dimSelect("client")}
          </Flex>
        </FilterCard>

        <FilterCard
          icon={<TeamOutlined />}
          title={t("reports.filtersStep.cards.projectManager")}
        >
          <Flex gap="middle" wrap="wrap">
            {dimSelect("pm")}
          </Flex>
        </FilterCard>

        <FilterCard
          icon={<TagsOutlined />}
          title={t("reports.filtersStep.cards.status")}
        >
          <Flex gap="middle" wrap="wrap">
            {dimSelect("status")}
            {dimSelect("state")}
          </Flex>
        </FilterCard>
      </Flex>

      <Card
        size="small"
        style={{ width: 300, flex: "none" }}
        title={t("reports.filtersStep.summary.title")}
      >
        <Flex vertical gap="middle">
          <Text type="secondary" style={{ fontSize: 13 }}>
            {t("reports.filtersStep.summary.subtitle")}
          </Text>
          {summaryRow(
            <CalendarOutlined />,
            t("reports.filtersStep.cards.dateRange"),
            definition?.compare
              ? `${windowSummary()} · ${t("reports.filtersStep.summary.compareTo", {
                  value:
                    definition.compare === "previous_period"
                      ? t("reports.compare.previousPeriod")
                      : t("reports.compare.samePeriodLastYear")
                })}`
              : windowSummary()
          )}
          {summaryRow(
            <FolderOutlined />,
            t("reports.filtersStep.cards.projects"),
            dimSummary("project")
          )}
          {summaryRow(
            <UserOutlined />,
            t("reports.filtersStep.cards.customers"),
            dimSummary("client")
          )}
          {summaryRow(
            <TeamOutlined />,
            t("reports.filtersStep.cards.projectManager"),
            dimSummary("pm")
          )}
          {summaryRow(
            <TagsOutlined />,
            t("reports.filtersStep.cards.status"),
            dimSummary("status")
          )}
          <Divider style={{ margin: "4px 0" }} />
          <Flex
            gap="small"
            style={{
              background: "rgba(42,125,140,.06)",
              borderRadius: 8,
              padding: 10
            }}
          >
            <InfoCircleOutlined style={{ color: neutral.muted, marginTop: 2 }} />
            <Text type="secondary" style={{ fontSize: 12 }}>
              {t("reports.filtersStep.summary.note")}
            </Text>
          </Flex>
        </Flex>
      </Card>
    </Flex>
  )
}
